import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { Info, Loader2 } from "lucide-react";

import { isFreePlan } from "@/components/shared/PaidPlanRoute";
import { FiscalProfileEditor, type FiscalProfileEditorRef } from "@/components/features/fiscal-profile/FiscalProfileEditor";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  fiscalProfileEditorValuesFromSources,
  toCreateFiscalProfilePayload,
  toExoneratedRegimePayload,
  toUpdateFiscalProfilePayload,
} from "@/lib/fiscalProfileEditorMappers";
import {
  getFiscalProfile,
  createFiscalProfile,
  updateFiscalProfile,
  type FiscalCompanyType,
} from "@/services/fiscalService";
import { updateTenantExoneratedRegime } from "@/services/authService";
import type { TenantCurrentResponse } from "@/services/authService";
import { tenantKeys } from "@/lib/queryKeys";

function isFiscalCompanyTypeFromTenant(
  v: string | null | undefined
): v is FiscalCompanyType {
  return (
    v === "unipersonal" || v === "sas" || v === "srl" || v === "sa"
  );
}

export interface SettingsFiscalProfileCardProps {
  tenant?: TenantCurrentResponse | null;
}

export function SettingsFiscalProfileCard({
  tenant,
}: SettingsFiscalProfileCardProps): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const editorRef = useRef<FiscalProfileEditorRef>(null);
  const hideAgendaFields = useMemo(
    () => tenant != null && isFreePlan(tenant.planId),
    [tenant]
  );
  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ["fiscal-profile"] as const,
    queryFn: getFiscalProfile,
    retry: false,
  });

  const [saving, setSaving] = useState(false);

  const initialValues = useMemo(
    () => fiscalProfileEditorValuesFromSources(profile ?? null, tenant ?? null),
    [profile, tenant]
  );

  const resetToken = useMemo(
    () =>
      `${profile?.id ?? "np"}-${profile?.updatedAt ?? ""}-${tenant?.exoneratedRegimeLegend ?? ""}-${String(tenant?.exoneratedRegimeEnabled)}-${String(tenant?.exoneratedRegimeHeaderLegend)}`,
    [profile?.id, profile?.updatedAt, tenant?.exoneratedRegimeLegend, tenant?.exoneratedRegimeEnabled, tenant?.exoneratedRegimeHeaderLegend]
  );

  const handleSubmit = useCallback(async () => {
    const editor = editorRef.current;
    if (!editor?.validate()) return;
    const values = editor.getValues();

    const taxesForApi: typeof values.taxes = hideAgendaFields
      ? profile
        ? profile.taxes.slice()
        : ["IVA"]
      : values.taxes;

    setSaving(true);

    // Profile and exoneration branding are two endpoints on two tables and
    // cannot be made atomic. Keep them in separate try blocks so a half-save
    // is reported as one, and re-hydrate on partial failure so the leyenda
    // fields stop showing input the server rejected.
    const refresh = async (): Promise<void> => {
      await queryClient.invalidateQueries({ queryKey: ["fiscal-profile"] });
      await queryClient.invalidateQueries({ queryKey: tenantKeys.current() });
    };

    try {
      try {
        if (profile) {
          await updateFiscalProfile(
            toUpdateFiscalProfilePayload(
              { ...values, taxes: taxesForApi },
              {
                hideAgendaFields,
                taxesWhenHidden: profile.taxes.slice(),
                bpsWhenHidden: profile.bpsCompanyNumber ?? null,
              }
            )
          );
        } else {
          await createFiscalProfile(
            toCreateFiscalProfilePayload(
              { ...values, taxes: taxesForApi },
              { hideAgendaFields }
            )
          );
        }
      } catch {
        // Nothing was written — the generic message is accurate here.
        toast.error(t("settings.fiscalProfile.error"));
        return;
      }

      try {
        // Built inside the guarded block on purpose: the mapper resolves the
        // leyenda against the industry option list and can throw, and that
        // failure belongs to the exoneration half like any other.
        await updateTenantExoneratedRegime(
          toExoneratedRegimePayload(values, tenant?.industry ?? null)
        );
      } catch {
        // The profile half is already committed. Say so, and resync.
        await refresh();
        toast.error(
          t(
            "settings.fiscalProfile.exonerationError",
            "Guardamos el perfil fiscal, pero no pudimos guardar la leyenda de régimen exonerado. Volvé a intentarlo."
          )
        );
        return;
      }

      await refresh();
      toast.success(
        profile
          ? t("settings.fiscalProfile.success")
          : t("settings.fiscalProfile.createSuccess")
      );
    } finally {
      setSaving(false);
    }
  }, [
    profile,
    hideAgendaFields,
    tenant?.industry,
    queryClient,
    t,
  ]);

  if (profileLoading) {
    return (
      <Card>
        <CardContent className="flex min-h-[200px] items-center justify-center py-8">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>{t("settings.fiscalProfile.saving")}</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-foreground">
          {t("settings.fiscalProfile.title")}
        </CardTitle>
        <CardDescription>
          {hideAgendaFields
            ? t("settings.fiscalProfile.descriptionFreePlan")
            : t("settings.fiscalProfile.description")}
        </CardDescription>
        {hideAgendaFields ? (
          <p className="text-sm text-muted-foreground">
            {t("settings.fiscalProfile.freePlanUpgradeHint")}{" "}
            <Button
              type="button"
              variant="link"
              className="h-auto p-0 text-sm font-medium"
              onClick={() => {
                navigate("/settings?tab=billing");
              }}
            >
              {t("settings.apiIntegration.upgradeCta")}
            </Button>
          </p>
        ) : null}
        {!profile && (
          <div
            role="status"
            className="flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900 dark:border-blue-800 dark:bg-blue-950/30 dark:text-blue-200"
          >
            <Info className="h-4 w-4 shrink-0 text-blue-600 dark:text-blue-400" />
            <span>{t("settings.fiscalProfile.noProfile")}</span>
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        <FiscalProfileEditor
          ref={editorRef}
          idPrefix="fiscal"
          initialValues={initialValues}
          resetToken={resetToken}
          hideCompanyType={
            tenant != null && isFiscalCompanyTypeFromTenant(tenant.companyType)
          }
          hideAgendaFields={hideAgendaFields}
          industryCode={tenant?.industry ?? null}
          industryForLegends={tenant?.industry ?? null}
          showSubmitButton={true}
          isSaving={saving}
          onSubmit={handleSubmit}
        />
      </CardContent>
    </Card>
  );
}
